import { action, computed, makeObservable, observable, toJS } from 'mobx';
import moment from 'moment';
import client from 'src/services/client';
import { IFoodicsSettings, IIntegrationSettings } from 'src/types/integrations';
import { LoadingStatus } from 'src/types/main';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { SyncStatus } from 'src/constants/integrations';
import integrationsStore from '~/mobx/integrationStore';

export class FoodicsIntegrationsStore {
  constructor() {
    makeObservable(this);
  }

  private getIntegrationAbortController = new AbortController();
  @observable public integrationLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public integrationSettings: IIntegrationSettings<IFoodicsSettings> | null = null;

  @action public getSettings = async () => {
    this.getIntegrationAbortController.abort();
    this.getIntegrationAbortController = getAbortController();

    try {
      this.integrationLoadingStatus = LoadingStatus.Loading;

      const res = await client.get('v3/integrations/foodics', { signal: this.getIntegrationAbortController.signal });

      this.integrationSettings = res.data;
      this.integrationLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.integrationLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'foodicsIntegrationStore getSettings', showModal: true });
    }
  };

  @action public updateSetting = async (name: string | number, value: any) => {
    if (!this.integrationSettings) return;
    this.integrationLoadingStatus = LoadingStatus.Refreshing;
    try {
      await client.patch('v3/integrations/foodics', { name, value });
      loop1: for (const [key] of Object.entries(this.integrationSettings.settings)) {
        if (key === 'essenceAdvancedIntegrationType') {
          for (const [integrationGroupName, integrationGroupSettings] of Object.entries(
            this.integrationSettings.settings.essenceAdvancedIntegrationType
          )) {
            for (const [integrationSettingName, _] of Object.entries(integrationGroupSettings)) {
              if (integrationSettingName === name) {
                this.integrationSettings.settings.essenceAdvancedIntegrationType[integrationGroupName][integrationSettingName] = value;
                break loop1;
              }
            }
          }
          continue;
        } else if (key === 'branches') {
          if (Array.isArray(value) && !value.length) {
            this.integrationSettings.settings.branches = this.integrationSettings.settings.branches.map((branch) => ({
              ...branch,
              is_selected: true,
            }));
            continue;
          } else {
            this.integrationSettings.settings.branches = this.integrationSettings.settings.branches.map((branch) => ({
              ...branch,
              is_selected: (value as string[]).includes(branch.id),
            }));
            continue;
          }
        }
        if (key === name) {
          // @ts-ignore
          this.integrationSettings.settings[key] = value;
          break;
        }
      }
      this.integrationLoadingStatus = LoadingStatus.Success;
      integrationsStore.fetchPosRestrictionsSettings();
    } catch (error: any) {
      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'foodicsIntegrationStore updateSetting', showModal: true });
    }
  };

  @computed get isLoading(): boolean {
    return [LoadingStatus.Init, LoadingStatus.Loading].includes(this.integrationLoadingStatus);
  }

  @computed get isRefreshing(): boolean {
    return [LoadingStatus.Refreshing].includes(this.integrationLoadingStatus);
  }

  @computed get getGroupsSelectOptions(): IPickerOption[] {
    if (!this.integrationSettings) {
      return [];
    }
    return this.integrationSettings.settings.groups.map((group) => {
      return {
        label: group.name,
        value: group.id,
      };
    });
  }

  @computed get getSelectedGroups(): IPickerOption[] {
    if (!this.integrationSettings) {
      return [];
    }
    return this.integrationSettings.settings.groups
      .filter((group) => group.id === this.integrationSettings?.settings.f5_menu_group)
      .map((group) => {
        return {
          label: group.name,
          value: group.id,
        };
      });
  }

  @computed get getNextSyncDate(): string {
    let nexSyncDate = moment().startOf('day').add('4', 'hours');
    if (moment().isAfter(nexSyncDate)) {
      nexSyncDate = nexSyncDate.add(1, 'day');
    }
    return nexSyncDate.format('DD/MM/YYYY, h:mm A');
  }

  @computed public get foodicsBranches(): IFoodicsSettings['branches'] {
    if (!this.integrationSettings?.settings || !this.integrationSettings.enabled) return [];

    return this.integrationSettings?.settings.branches;
  }

  @computed public get foodicsSelectedBranches(): IFoodicsSettings['branches'] {
    return this.foodicsBranches.filter((branch) => branch.is_selected);
  }

  @action public runSync = async () => {
    if (!this.integrationSettings) {
      return;
    }
    try {
      this.integrationLoadingStatus = LoadingStatus.Refreshing;
      await client.post('v3/integrations/foodics/sync');
      this.integrationLoadingStatus = LoadingStatus.Success;
      this.integrationSettings.settings.sync_status = SyncStatus.PENDING;
    } catch (error) {
      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'foodicsIntegrationStore runSync', showModal: true });
    }
  };

  @action public logout = async () => {
    try {
      this.integrationLoadingStatus = LoadingStatus.Loading;
      await client.post('v3/integrations/foodics/logout');
      this.integrationLoadingStatus = LoadingStatus.Success;
    } catch (error) {
      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'foodicsIntegrationStore logout', showModal: true });
    }
  };

  @action public unMount = () => {
    this.getIntegrationAbortController.abort();
    this.integrationLoadingStatus = LoadingStatus.Init;
  };
}

const foodicsIntegrationStore = new FoodicsIntegrationsStore();
export default foodicsIntegrationStore;
